import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { BLOG_POSTS } from "../data/mock";
import { useRevealGroup } from "../hooks/useReveal";
import { PageHero, CTASection } from "../components/Shared";

const CATEGORIES = ["All", ...new Set(BLOG_POSTS.map((p) => p.category))];

export default function Blog() {
  const ref = useRevealGroup();
  const [filter, setFilter] = useState("All");
  const posts = filter === "All" ? BLOG_POSTS : BLOG_POSTS.filter((p) => p.category === filter);

  return (
    <div ref={ref}>
      <PageHero
        badge="Our Blog"
        title="Insurance Insights, Made Simple"
        subtitle="Practical, plain-language guides to help you make confident decisions about Super Visa, life, critical illness and disability coverage in Canada."
        image="https://images.unsplash.com/photo-1637763723578-79a4ca9225f7?q=80&w=1600"
      />

      <section className="bg-[#F4F7FB] py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-wrap items-center justify-center gap-3" data-reveal>
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`blog-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-all duration-300 ${
                  filter === c ? "bg-[#006FB9] text-white shadow-md" : "bg-white text-[#294A65] hover:bg-[#E2F2FA]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post, i) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                data-reveal
                style={{ transitionDelay: `${i * 80}ms` }}
                data-testid={`blog-list-card-${post.slug}`}
                className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-[0_2px_16px_rgba(0,111,185,0.07)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_48px_rgba(0,111,185,0.15)]"
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={post.image} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3.5 py-1 text-xs font-bold text-[#006FB9]">{post.category}</span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <p className="text-xs font-medium text-[#70879A]">{post.date} · {post.readTime}</p>
                  <h3 className="mt-2.5 font-heading text-lg font-bold leading-snug text-[#0B2748] transition-colors duration-300 group-hover:text-[#0785C7]">{post.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-[#526A7D]">{post.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#006FB9]">
                    Read Article <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection title="Questions About Your Coverage?" />
    </div>
  );
}
